import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useEffect, useState, type MouseEvent } from 'react';
import { sendGoogleAdsConversion } from '../utils/googleAds';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../hooks/useAnalytics';

const css = `
  .landing * { margin: 0; padding: 0; box-sizing: border-box; }
  .landing {
    font-family: 'Inter', sans-serif;
    color: #2a2a2a;
    min-height: 100vh;
    background: #fafaf8;
    overflow-x: hidden;
  }
  .landing .scene-section {
    position: relative;
    overflow: hidden;
  }
  .landing-video-bg {
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    object-fit: cover;
    z-index: 0;
    pointer-events: none;
  }
  .landing-video-overlay {
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    background: linear-gradient(to right, rgba(250,250,248,0.72) 0%, rgba(250,250,248,0.15) 60%, rgba(250,250,248,0) 100%);
    z-index: 1;
    pointer-events: none;
  }
  .landing .scene-section > *:not(.landing-video-bg):not(.landing-video-overlay) {
    position: relative;
    z-index: 2;
  }

  /* ── CSS variables ── */
  .landing {
    --purple: #5c4d96;
    --green: #5aaa8a;
    --bg: #fafaf8;
    --text: #1a1a2e;
    --muted: #5a5a7a;
  }

  /* ── Nav ── */
  .landing nav {
    position: sticky; top: 0; width: 100%; z-index: 100;
    padding: 20px 32px;
    display: flex; justify-content: space-between; align-items: center;
    background: rgba(250,250,248,0.85);
    backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(0,0,0,0.06);
  }
  .landing nav .logo { font-weight: 800; font-size: 22px; color: #5c4d96; }
  .landing nav .nav-link {
    font-size: 14px; font-weight: 500; color: #5a5a7a;
    text-decoration: none; cursor: pointer; transition: color 0.2s;
  }
  .landing nav .nav-link:hover { color: #5c4d96; }
  .landing nav .cta-nav {
    background: rgba(92,77,150,0.08); color: #5c4d96;
    border: 1px solid rgba(92,77,150,0.25);
    padding: 9px 22px; border-radius: 20px; font-weight: 600; font-size: 14px;
    cursor: pointer; text-decoration: none;
    transition: background 0.2s;
  }
  .landing nav .cta-nav:hover { background: rgba(92,77,150,0.14); }

  /* ── Hero scene ── */
  .landing .scene-section {
    width: 100%; padding: 80px 24px 60px;
  }
  .landing .scene-hero-text { max-width: 640px; margin: 0 0 0 8%; }
  .landing .hero-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 14px; border-radius: 999px;
    background: rgba(92,77,150,0.08); border: 1px solid rgba(92,77,150,0.16);
    color: #5c4d96; font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
    text-transform: uppercase; margin-bottom: 18px;
  }
  .landing .hero-eyebrow-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: linear-gradient(135deg, #5c4d96, #5aaa8a);
    box-shadow: 0 0 0 4px rgba(90,170,138,0.12);
  }
  @keyframes heroFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes headlineColorCycle {
    0%   { color: #1a1a2e; }
    20%  { color: #2c1e5e; }
    40%  { color: #1a3a2e; }
    60%  { color: #3a1a2e; }
    80%  { color: #1a2e3a; }
    100% { color: #1a1a2e; }
  }
  @keyframes sublineColorCycle {
    0%   { color: #5a5a7a; }
    20%  { color: #5c4d96; }
    40%  { color: #2f7f62; }
    60%  { color: #b7891f; }
    80%  { color: #b15345; }
    100% { color: #5a5a7a; }
  }
  .landing .scene-hero-text h1 {
    font-size: clamp(36px, 5vw, 60px); font-weight: 800;
    line-height: 1.05; margin-bottom: 24px;
    animation: landingFadeUp 1s ease-out;
    background: linear-gradient(135deg, #6b3fa0, #0d1b4a);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .landing .scene-hero-text h1 .accent {
    background: linear-gradient(135deg, #6b3fa0, #0d1b4a);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .landing .scene-hero-text p {
    font-size: 20px; color: var(--muted); max-width: 400px; line-height: 1.3; font-weight: 500;
    margin-bottom: 32px; animation: landingFadeUp 1s ease-out 0.3s both;
    font-family: 'Inter', sans-serif;
  }
  .landing .hero-proof {
    display: flex; flex-wrap: wrap; gap: 10px;
    margin: -8px 0 14px;
  }
  .landing .hero-proof-chip {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 14px; border-radius: 14px; background: #fff;
    border: 1px solid rgba(92,77,150,0.12); box-shadow: 0 12px 28px rgba(26,26,46,0.07);
    font-size: 13px; font-weight: 700; color: #384255;
  }
  .landing .hero-proof-chip strong { color: var(--text); font-weight: 800; }
  .landing .hero-trust-row {
    display: flex; flex-wrap: wrap; gap: 8px;
    margin: 0 0 26px;
  }
  .landing .hero-trust-item {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 13px; border-radius: 999px;
    background: rgba(255,255,255,0.92);
    border: 1px solid rgba(26,26,46,0.09);
    box-shadow: 0 4px 14px rgba(26,26,46,0.06);
    font-size: 13px; font-weight: 600; color: #384255;
  }
  .landing .hero-trust-item.hipaa { color: #1f6b44; border-color: rgba(31,107,68,0.18); background: rgba(240,255,247,0.95); }
  .landing .hero-trust-item svg { width: 14px; height: 14px; flex-shrink: 0; }
  .landing .scene-hero-text .cta {
    background: #5c4d96; color: #fff; padding: 16px 48px;
    border-radius: 28px; font-weight: 700; font-size: 16px;
    text-decoration: none; display: inline-block; cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    animation: landingFadeUp 1s ease-out 0.5s both;
    box-shadow: 0 4px 20px rgba(124,107,196,0.35);
  }
  .landing .scene-hero-text .cta:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(124,107,196,0.45); }
  /* ── Stat strip ── */
  .landing .stat-strip {
    display: flex; align-items: stretch; justify-content: center; flex-wrap: wrap;
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 20px;
    margin: 0 auto 48px; max-width: 860px; overflow: hidden;
    box-shadow: 0 4px 24px rgba(0,0,0,0.05);
  }
  .landing .stat-item {
    flex: 1; min-width: 150px; padding: 26px 20px; text-align: center;
    border-right: 1px solid rgba(0,0,0,0.07);
  }
  .landing .stat-item:last-child { border-right: none; }
  .landing .stat-number {
    font-size: 30px; font-weight: 800;
    background: linear-gradient(135deg, #5c4d96, #5aaa8a);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    line-height: 1; margin-bottom: 5px;
  }
  .landing .stat-label { font-size: 12.5px; color: var(--muted); font-weight: 500; line-height: 1.4; }

  /* ── Testimonials ── */
  .landing .testimonials-section {
    max-width: 1100px; margin: 0 auto; padding: 40px 24px 20px;
  }
  .landing .testimonials-header { text-align: center; margin-bottom: 40px; }
  .landing .testimonials-header h2 { font-size: clamp(26px, 4vw, 38px); font-weight: 800; color: var(--text); }
  .landing .testimonials-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;
  }
  .landing .testimonial-card {
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 20px; padding: 26px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05); display: flex; flex-direction: column; gap: 14px;
  }
  .landing .testimonial-stars { color: #f5b83d; font-size: 15px; letter-spacing: 1px; }
  .landing .testimonial-quote {
    font-size: 15px; line-height: 1.7; color: var(--text); font-style: italic; flex: 1;
  }
  .landing .testimonial-author { display: flex; align-items: center; gap: 12px; }
  .landing .testimonial-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
  }
  .landing .testimonial-name { font-size: 14px; font-weight: 700; color: var(--text); }
  .landing .testimonial-cred { font-size: 12px; color: var(--muted); }

  .landing .trust-band {
    max-width: 1100px; margin: 0 auto 48px;
    padding: 0 24px;
  }
  .landing .trust-band-grid {
    display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .landing .trust-band-card {
    background: #fff; border: 1px solid rgba(26,26,46,0.08);
    border-radius: 18px; padding: 18px 18px 16px;
    box-shadow: 0 12px 28px rgba(26,26,46,0.05);
  }
  .landing .trust-band-label {
    font-size: 11px; font-weight: 800; letter-spacing: 0.08em;
    text-transform: uppercase; color: #5c4d96; margin-bottom: 10px;
  }
  .landing .trust-band-title {
    font-size: 18px; font-weight: 800; color: var(--text);
    margin-bottom: 8px; letter-spacing: -0.02em;
  }
  .landing .trust-band-copy {
    font-size: 14px; line-height: 1.65; color: #5a5a7a;
  }

  /* ── Mobile hero ── */
  .landing .mobile-hero { display: none; }

  /* ── Scroll reveal ── */
  .landing .reveal {
    opacity: 0; transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .landing .reveal.from-left  { transform: translateX(-60px); }
  .landing .reveal.from-right { transform: translateX(60px); }
  .landing .reveal.visible    { opacity: 1; transform: translate(0); }

  /* ── Section divider ── */
  .landing .section-divider {
    width: 1px; height: 60px;
    background: linear-gradient(180deg, transparent, rgba(124,107,196,0.2), transparent);
    margin: 0 auto;
  }

  /* ── Story sections ── */
  .landing .story-section {
    min-height: 80vh; display: flex; align-items: center;
    padding: 80px; max-width: 1200px; margin: 0 auto; gap: 80px;
  }
  .landing .story-section:nth-child(even) { flex-direction: row-reverse; }
  .landing .story-text { flex: 1; }
  .landing .story-visual { flex: 1; }
  .landing .step-number {
    font-size: 120px; font-weight: 800; color: rgba(124,107,196,0.07);
    line-height: 1; margin-bottom: -20px; letter-spacing: -4px; user-select: none;
  }
  .landing .step-label {
    font-size: 11px; font-weight: 700; letter-spacing: 2px; color: #5c4d96;
    text-transform: uppercase; margin-bottom: 14px;
  }
  .landing .story-text h2 {
    font-size: 44px; font-weight: 800; letter-spacing: -1.5px;
    color: var(--text); margin-bottom: 18px; line-height: 1.1;
  }
  .landing .story-text p { font-size: 17px; color: var(--muted); line-height: 1.7; max-width: 420px; }

  /* ── Recording card ── */
  .landing .recording-card {
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 24px; padding: 36px;
    box-shadow: 0 8px 40px rgba(124,107,196,0.10);
    display: flex; flex-direction: column; align-items: center; gap: 20px;
  }
  .landing .mic-circle {
    position: relative; width: 90px; height: 90px;
    display: flex; align-items: center; justify-content: center;
  }
  .landing .pulse-ring {
    position: absolute; border-radius: 50%;
    border: 2px solid rgba(124,107,196,0.25);
    animation: landingRingPulse 2s ease-out infinite;
  }
  .landing .pulse-ring:nth-child(1) { width: 90px;  height: 90px;  animation-delay: 0s; }
  .landing .pulse-ring:nth-child(2) { width: 110px; height: 110px; animation-delay: 0.5s; }
  .landing .pulse-ring:nth-child(3) { width: 130px; height: 130px; animation-delay: 1s; }
  @keyframes landingRingPulse {
    0%   { opacity: 0.8; transform: scale(0.9); }
    100% { opacity: 0;   transform: scale(1.1); }
  }
  .landing .mic-inner {
    width: 64px; height: 64px; border-radius: 50%;
    background: linear-gradient(135deg, #5c4d96, #9b8ae8);
    display: flex; align-items: center; justify-content: center;
    font-size: 28px; position: relative; z-index: 2;
    box-shadow: 0 4px 16px rgba(124,107,196,0.4);
  }
  .landing .recording-bars { display: flex; align-items: center; gap: 3px; height: 40px; }
  .landing .rec-bar {
    width: 5px; border-radius: 3px;
    background: linear-gradient(180deg, #5c4d96, #5aaa8a);
    animation-timing-function: ease-in-out; animation-iteration-count: infinite; animation-direction: alternate;
  }
  .landing .rec-bar:nth-child(1)  { animation: landingRecA 0.60s infinite alternate; }
  .landing .rec-bar:nth-child(2)  { animation: landingRecB 0.75s infinite alternate; animation-delay: 0.10s; }
  .landing .rec-bar:nth-child(3)  { animation: landingRecC 0.55s infinite alternate; animation-delay: 0.20s; }
  .landing .rec-bar:nth-child(4)  { animation: landingRecA 0.80s infinite alternate; animation-delay: 0.05s; }
  .landing .rec-bar:nth-child(5)  { animation: landingRecB 0.65s infinite alternate; animation-delay: 0.15s; }
  .landing .rec-bar:nth-child(6)  { animation: landingRecC 0.70s infinite alternate; animation-delay: 0.25s; }
  .landing .rec-bar:nth-child(7)  { animation: landingRecA 0.58s infinite alternate; animation-delay: 0.08s; }
  .landing .rec-bar:nth-child(8)  { animation: landingRecB 0.85s infinite alternate; animation-delay: 0.18s; }
  .landing .rec-bar:nth-child(9)  { animation: landingRecC 0.62s infinite alternate; animation-delay: 0.30s; }
  .landing .rec-bar:nth-child(10) { animation: landingRecA 0.72s infinite alternate; animation-delay: 0.12s; }
  @keyframes landingRecA { from { height: 6px; }  to { height: 32px; } }
  @keyframes landingRecB { from { height: 12px; } to { height: 24px; } }
  @keyframes landingRecC { from { height: 18px; } to { height: 36px; } }
  .landing .device-chips {
    display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; margin-top: 4px;
  }
  .landing .device-chip {
    display: flex; align-items: center; gap: 6px;
    background: #f4f2fb; border: 1px solid rgba(92,77,150,0.15);
    border-radius: 20px; padding: 6px 14px;
    font-size: 12px; font-weight: 600; color: #5c4d96;
    white-space: nowrap;
  }
  .landing .device-chip span { font-size: 15px; }
  .landing .recording-label-row {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 700; color: #e05252;
    letter-spacing: 1px; text-transform: uppercase;
  }
  .landing .rec-dot-small {
    width: 8px; height: 8px; border-radius: 50%; background: #e05252;
    animation: landingRecPulse2 1s ease-in-out infinite;
  }
  @keyframes landingRecPulse2 { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

  /* ── Transcript card ── */
  .landing .transcript-card {
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 24px; padding: 28px;
    box-shadow: 0 8px 40px rgba(124,107,196,0.08);
  }
  .landing .transcript-card-header {
    font-size: 11px; font-weight: 700; letter-spacing: 1px; color: #5c4d96;
    text-transform: uppercase; margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
  }
  .landing .ai-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #5aaa8a;
    animation: landingAiPulse 1.5s ease-in-out infinite;
  }
  @keyframes landingAiPulse {
    0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); }
  }
  .landing .transcript-words { font-size: 14px; line-height: 1.7; color: var(--text); }
  .landing .tw { opacity: 0; }
  @keyframes landingWordFade {
    0%  { opacity: 0; } 8%  { opacity: 1; } 92% { opacity: 1; } 100%{ opacity: 1; }
  }
  .landing .tw:nth-child(1)  { animation: landingWordFade 4s 0.2s infinite; }
  .landing .tw:nth-child(2)  { animation: landingWordFade 4s 0.5s infinite; }
  .landing .tw:nth-child(3)  { animation: landingWordFade 4s 0.8s infinite; }
  .landing .tw:nth-child(4)  { animation: landingWordFade 4s 1.1s infinite; }
  .landing .tw:nth-child(5)  { animation: landingWordFade 4s 1.4s infinite; }
  .landing .tw:nth-child(6)  { animation: landingWordFade 4s 1.7s infinite; }
  .landing .tw:nth-child(7)  { animation: landingWordFade 4s 2.0s infinite; }
  .landing .tw:nth-child(8)  { animation: landingWordFade 4s 2.3s infinite; }
  .landing .tw:nth-child(9)  { animation: landingWordFade 4s 2.6s infinite; }
  .landing .tw:nth-child(10) { animation: landingWordFade 4s 2.9s infinite; }
  .landing .tw:nth-child(11) { animation: landingWordFade 4s 3.2s infinite; }
  .landing .tw:nth-child(12) { animation: landingWordFade 4s 3.5s infinite; }
  .landing .tw.hi { color: #5c4d96; font-weight: 600; }

  /* ── SOAP card ── */
  .landing .soap-card {
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 24px; padding: 28px;
    box-shadow: 0 8px 40px rgba(124,107,196,0.08); display: flex; flex-direction: column; gap: 10px;
  }
  .landing .soap-card-header { font-size: 11px; font-weight: 700; letter-spacing: 1px; color: #5c4d96; text-transform: uppercase; margin-bottom: 4px; }
  .landing .sc-row { display: flex; gap: 12px; align-items: flex-start; padding: 10px 12px; border-radius: 12px; background: #f8f8fb; }
  .landing .sc-row.sc-s { border-left: 3px solid #5c4d96; }
  .landing .sc-row.sc-o { border-left: 3px solid #5aaa8a; }
  .landing .sc-row.sc-a { border-left: 3px solid #f0be50; }
  .landing .sc-row.sc-p { border-left: 3px solid #e86060; }
  .landing .sc-tag { font-size: 12px; font-weight: 800; min-width: 18px; padding-top: 1px; }
  .landing .sc-row.sc-s .sc-tag { color: #5c4d96; }
  .landing .sc-row.sc-o .sc-tag { color: #5aaa8a; }
  .landing .sc-row.sc-a .sc-tag { color: #c09020; }
  .landing .sc-row.sc-p .sc-tag { color: #c04040; }
  .landing .sc-text { font-size: 13px; color: var(--text); line-height: 1.45; }

  /* ── Billing card ── */
  .landing .billing-card {
    background: white; border: 1px solid rgba(0,0,0,0.07); border-radius: 24px; padding: 28px;
    box-shadow: 0 8px 40px rgba(124,107,196,0.08); display: flex; flex-direction: column; gap: 12px;
  }
  .landing .billing-card-header { font-size: 11px; font-weight: 700; letter-spacing: 1px; color: #5aaa8a; text-transform: uppercase; margin-bottom: 4px; }
  .landing .bc-row { background: #f8f8fb; border-radius: 12px; padding: 12px 14px; }
  .landing .bc-top { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
  .landing .bc-code { font-size: 14px; font-weight: 800; color: #5c4d96; min-width: 56px; }
  .landing .bc-desc { flex: 1; font-size: 12.5px; color: var(--text); }
  .landing .bc-pct { font-size: 13px; font-weight: 700; color: #5aaa8a; }
  .landing .bc-bar-track { height: 4px; background: rgba(0,0,0,0.07); border-radius: 2px; overflow: hidden; }
  .landing .bc-bar-fill { height: 100%; border-radius: 2px; background: linear-gradient(90deg, #5c4d96, #5aaa8a); }
  .landing .copy-btn {
    margin-top: 4px; padding: 10px 20px; background: #5aaa8a; color: white;
    font-weight: 700; font-size: 13px; border-radius: 20px; text-align: center;
    cursor: pointer; border: none; transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 3px 12px rgba(90,170,138,0.3);
  }
  .landing .copy-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(90,170,138,0.4); }

  /* ── Final CTA ── */
  .landing .final-cta { text-align: center; padding: 100px 32px 120px; }
  .landing .final-cta h2 { font-size: 48px; font-weight: 800; letter-spacing: -1.5px; color: var(--text); margin-bottom: 16px; }
  .landing .final-cta p { font-size: 18px; color: var(--muted); margin-bottom: 36px; }
  .landing .btn-purple {
    display: inline-block; padding: 14px 34px; background: #5c4d96; color: white;
    font-weight: 700; font-size: 15px; border-radius: 28px; text-decoration: none; cursor: pointer;
    box-shadow: 0 4px 20px rgba(124,107,196,0.35); transition: transform 0.2s, box-shadow 0.2s;
  }
  .landing .btn-purple:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(124,107,196,0.45); }

  /* ── FAQ ── */
  .landing .faq-section {
    max-width: 720px; margin: 0 auto; padding: 80px 24px;
  }
  .landing .faq-header { text-align: center; margin-bottom: 40px; }
  .landing .faq-header h2 { font-size: clamp(28px, 4vw, 42px); font-weight: 800; margin-top: 8px; }
  .landing .faq-list { display: flex; flex-direction: column; gap: 8px; }
  .landing .faq-item {
    background: rgba(255,255,255,0.65);
    backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.7); border-radius: 16px;
    overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.04); transition: box-shadow 0.2s;
  }
  .landing .faq-item:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  .landing .faq-q {
    width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 18px 20px; background: none; border: none; cursor: pointer;
    text-align: left; font-family: 'Inter', sans-serif;
    font-size: 15px; font-weight: 600; color: var(--text); line-height: 1.4;
  }
  .landing .faq-chevron {
    flex-shrink: 0; width: 26px; height: 26px; border-radius: 50%;
    background: rgba(92,77,150,0.1); color: var(--purple);
    display: flex; align-items: center; justify-content: center;
    transition: transform 0.25s, background 0.2s;
  }
  .landing .faq-chevron svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
  .landing .faq-item.open .faq-chevron { transform: rotate(180deg); background: rgba(92,77,150,0.2); }
  .landing .faq-a { max-height: 0; overflow: hidden; padding: 0 20px; transition: max-height 0.3s ease, padding 0.3s ease; }
  .landing .faq-item.open .faq-a { max-height: 300px; padding: 0 20px 18px; }
  .landing .faq-a p { font-size: 15px; color: #555; line-height: 1.7; border-top: 1px solid rgba(0,0,0,0.06); padding-top: 14px; }

  /* ── Footer ── */
  .landing footer {
    text-align: center; padding: 28px 24px; font-size: 13px; color: var(--muted); opacity: 0.6;
    border-top: 1px solid rgba(0,0,0,0.06);
    display: flex; flex-direction: column; align-items: center; gap: 8px;
  }
  .landing footer .footer-links { display: flex; gap: 16px; }
  .landing footer .footer-links a { color: var(--muted); text-decoration: none; transition: color 0.2s; }
  .landing footer .footer-links a:hover { color: #5c4d96; }

  /* ── Keyframes ── */
  @keyframes landingFadeUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes landingWaveMotion {
    0%, 100% { transform: scaleY(0.6); } 50% { transform: scaleY(1); }
  }

  /* ── Responsive ── */
  @media (max-width: 700px) {
    .landing .scene-hero-text { display: none; }
    .landing .scene-section { padding: 220px 24px 60px; height: 65vh; }
    .landing-video-overlay { background: linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%); }
    .landing .mobile-hero {
      display: flex; flex-direction: column; align-items: center; text-align: center;
    }
    .landing .mobile-hero-h1 {
      font-size: 42px; font-weight: 800; line-height: 1.05; margin-bottom: 20px;
      color: #fff; background: none; -webkit-text-fill-color: #fff;
      animation: heroFadeUp 1.2s cubic-bezier(0.22,1,0.36,1) both;
    }
    .landing .mobile-hero-h1 .accent {
      background: none; color: #fff; -webkit-text-fill-color: #fff;
    }
    .landing .mobile-hero p {
      font-size: 20px; color: #fff; font-weight: 800; line-height: 1.3; margin-bottom: 28px; max-width: 400px;
      animation: heroFadeUp 2s cubic-bezier(0.22,1,0.36,1) 0.5s both;
    }
    .landing .mobile-hero .cta {
      background: #5c4d96; color: #fff; padding: 14px 40px;
      border-radius: 28px; font-weight: 700; font-size: 15px;
      text-decoration: none; display: inline-block; cursor: pointer;
    }
    .landing .hero-trust-row { justify-content: center; }
    .landing .trust-band-grid { grid-template-columns: 1fr; }
    .landing .story-section { flex-direction: column !important; padding: 60px 24px; gap: 40px; min-height: auto; }
    .landing .story-text p { max-width: 100%; }
    .landing .step-number { font-size: 80px; }
    .landing .story-text h2 { font-size: 34px; }
    .landing .final-cta h2 { font-size: 34px; }
    .landing .final-cta { padding: 60px 24px 80px; }
  }
`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { continueAsGuest } = useAuth();
  const [guestLoading, setGuestLoading] = useState(false);

  const handleContinueAsGuest = async () => {
    setGuestLoading(true);
    try { await continueAsGuest(); }
    catch { navigate('/login'); }
    finally { setGuestLoading(false); }
  };

  useEffect(() => {
    if (!document.querySelector('link[href*="Inter"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    // Scroll reveal
    const observer = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.landing .reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const SIGN_IN_LABEL = 'sign_in_click';
  const TRY_FOR_FREE_LABEL = 'try_for_free_click';

  const handleLoginClick =
    (label: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      sendGoogleAdsConversion(label);
      track('sign_in_click', { location: label });
      navigate('/login');
    };

  const handleTryFree = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    sendGoogleAdsConversion(TRY_FOR_FREE_LABEL);
    track('try_for_free_click');
    await handleContinueAsGuest();
  };

  const toggleFaq = (e: MouseEvent<HTMLButtonElement>) => {
    const item = (e.currentTarget as HTMLElement).closest('.faq-item') as HTMLElement;
    const isOpen = item.classList.contains('open');
    document.querySelectorAll('.landing .faq-item.open').forEach(el => el.classList.remove('open'));
    if (!isOpen) {
      item.classList.add('open');
      const question = item.querySelector('.faq-q')?.textContent?.trim();
      track('faq_open', { question });
    }
  };

  return (
    <Box className="landing">
      <style>{css}</style>

      <nav>
        <div className="logo">AirScribe</div>
        <div style={{display:'flex', alignItems:'center', gap:'24px'}}>
          <a href="/login" className="nav-link" onClick={handleTryFree}>Try for free</a>
          <a href="/login" className="cta-nav" onClick={handleLoginClick(SIGN_IN_LABEL)}>Sign In</a>
        </div>
      </nav>

      <main>
      {/* ── Hero ── */}
      <div className="scene-section">
        <video className="landing-video-bg" autoPlay muted loop playsInline>
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="landing-video-overlay" />
        <div className="scene-hero-text">
          <h1>Your AI<br /><span className="accent">medical scribe.</span></h1>
          <p>Speak naturally after any encounter. AirScribe turns your voice into structured SOAP notes and billing codes — ready to copy into your EHR in under 60 seconds.</p>
          <div className="hero-trust-row">
            <div className="hero-trust-item hipaa">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              HIPAA compliant
            </div>
            <div className="hero-trust-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Encrypted in transit &amp; at rest
            </div>
            <div className="hero-trust-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              No training on patient data
            </div>
            <div className="hero-trust-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Free to start
            </div>
          </div>
          <a href="/login" className="cta" onClick={handleTryFree}>{guestLoading ? 'Setting up…' : 'Get Started'}</a>
        </div>
        <div className="mobile-hero">
          <div className="mobile-hero-h1">Your AI<br /><span className="accent">medical scribe.</span></div>
          <p>Dictate after any encounter. Get a full SOAP note in seconds.</p>
          <a href="/login" className="cta" onClick={handleTryFree}>Get Started</a>
        </div>
      </div>

      {/* ── Testimonials ── */}
      <div className="testimonials-section">
        <div className="testimonials-header reveal">

          <h2>What clinicians say</h2>
        </div>
        <div className="testimonials-grid">
          <div className="testimonial-card reveal">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-quote">"I finish notes before I even reach the next room. It captures the clinical nuance I'd spend 15 minutes writing — automatically."</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar" style={{background:'#e8e0f8'}}>👩‍⚕️</div>
              <div>
                <div className="testimonial-name">Dr. Sarah M.</div>
                <div className="testimonial-cred">Family Medicine, California</div>
              </div>
            </div>
          </div>
          <div className="testimonial-card reveal">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-quote">"The billing code suggestions alone save me 30 minutes of lookup time every shift. I finally leave on time."</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar" style={{background:'#d8f0e8'}}>🧑‍⚕️</div>
              <div>
                <div className="testimonial-name">James T., PA-C</div>
                <div className="testimonial-cred">Urgent Care, Texas</div>
              </div>
            </div>
          </div>
          <div className="testimonial-card reveal">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-quote">"I was skeptical about AI documentation, but the SOAP quality is genuinely impressive. My notes are more thorough now than before."</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar" style={{background:'#f8e8e0'}}>👩‍⚕️</div>
              <div>
                <div className="testimonial-name">Dr. Priya K.</div>
                <div className="testimonial-cred">Internal Medicine, New York</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Story Sections ── */}
      <div className="section-divider"></div>

      <div className="story-section">
        <div className="story-text reveal from-left">
          <div className="step-number">01</div>
          <div className="step-label">Apple Watch · iPhone · USB Recorder</div>
          <h2>Record from anywhere.</h2>
          <p>Dictate on your Apple Watch during the encounter. Record on your iPhone. Or plug any USB voice recorder into your iPhone to upload notes from the field — no Wi-Fi required. Speak naturally, no templates needed.</p>
        </div>
        <div className="story-visual reveal from-right">
          <div className="recording-card">
            <div className="mic-circle">
              <div className="pulse-ring"></div><div className="pulse-ring"></div><div className="pulse-ring"></div>
              <div className="mic-inner">🎙️</div>
            </div>
            <div className="recording-bars">
              <div className="rec-bar"></div><div className="rec-bar"></div><div className="rec-bar"></div>
              <div className="rec-bar"></div><div className="rec-bar"></div><div className="rec-bar"></div>
              <div className="rec-bar"></div><div className="rec-bar"></div><div className="rec-bar"></div>
              <div className="rec-bar"></div>
            </div>
            <div className="recording-label-row"><div className="rec-dot-small"></div>Recording...</div>
            <div className="device-chips">
              <div className="device-chip"><span>⌚</span>Apple Watch</div>
              <div className="device-chip"><span>📱</span>iPhone</div>
              <div className="device-chip"><span>🎙️</span>USB Recorder</div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="story-section">
        <div className="story-visual reveal from-left">
          <div className="transcript-card">
            <div className="transcript-card-header"><div className="ai-dot"></div>Live Transcript</div>
            <div className="transcript-words">
              <span className="tw">Patient </span><span className="tw hi">reports </span><span className="tw">lower </span>
              <span className="tw hi">back pain </span><span className="tw">rated </span><span className="tw hi">7 out of 10,</span>
              <span className="tw"> radiating to </span><span className="tw hi">left leg </span><span className="tw">since </span>
              <span className="tw hi">Monday.</span><span className="tw"> Positive straight leg raise </span><span className="tw">noted on the left side.</span>
            </div>
          </div>
        </div>
        <div className="story-text reveal from-right">
          <div className="step-number">02</div>
          <div className="step-label">Step two</div>
          <h2>Every word, captured.</h2>
          <p>AirScribe transcribes in real time with high accuracy. Key clinical terms are automatically identified and highlighted for structured extraction.</p>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="story-section">
        <div className="story-text reveal from-left">
          <div className="step-number">03</div>
          <div className="step-label">Step three</div>
          <h2>Structured, instantly.</h2>
          <p>Your freeform speech becomes a complete SOAP note — Subjective, Objective, Assessment, and Plan — organized and ready for your EHR.</p>
        </div>
        <div className="story-visual reveal">
          <div className="soap-card">
            <div className="soap-card-header">SOAP Note</div>
            <div className="sc-row sc-s"><span className="sc-tag">S</span><span className="sc-text">44F c/o 3-week Hx of progressive LBP (7/10), L &gt; R, radiating to posterior L leg to knee. Worse with flexion and prolonged sitting. Denies bowel/bladder changes, saddle anesthesia, or fever. No prior lumbar pathology.</span></div>
            <div className="sc-row sc-o"><span className="sc-tag">O</span><span className="sc-text">BP 128/82, HR 74. Positive SLR at 45° left. Reduced lumbar flexion (fingertips to mid-shin). L4-L5 paraspinal tenderness bilaterally. Mild weakness dorsiflexion L foot (4/5). DTRs: patellar 2+/2+, Achilles 1+ L vs 2+ R.</span></div>
            <div className="sc-row sc-a"><span className="sc-tag">A</span><span className="sc-text">L4-L5 disc herniation with left-sided radiculopathy (M51.16). DDx: piriformis syndrome, facet arthropathy, lateral recess stenosis.</span></div>
            <div className="sc-row sc-p"><span className="sc-tag">P</span><span className="sc-text">1. Naproxen 500mg BID ×14d w/ food. 2. Cyclobenzaprine 5mg QHS PRN spasm. 3. MRI L-spine w/o contrast ordered. 4. PT referral — McKenzie method. 5. F/U 3–4 wks or sooner if neuro sx worsen. 6. Return precautions: bowel/bladder dysfunction, bilateral leg weakness.</span></div>
          </div>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="story-section">
        <div className="story-visual reveal from-right">
          <div className="billing-card">
            <div className="billing-card-header">Suggested Billing Codes</div>
            <div className="bc-row">
              <div className="bc-top"><span className="bc-code">99214</span><span className="bc-desc">Office/outpatient visit, moderate complexity</span><span className="bc-pct">97%</span></div>
              <div className="bc-bar-track"><div className="bc-bar-fill" style={{width:'97%'}}></div></div>
            </div>
            <div className="bc-row">
              <div className="bc-top"><span className="bc-code">M51.16</span><span className="bc-desc">IVD degeneration, lumbar w/ radiculopathy</span><span className="bc-pct">99%</span></div>
              <div className="bc-bar-track"><div className="bc-bar-fill" style={{width:'99%'}}></div></div>
            </div>
            <div className="bc-row">
              <div className="bc-top"><span className="bc-code">72148</span><span className="bc-desc">MRI lumbar spine w/o contrast</span><span className="bc-pct">94%</span></div>
              <div className="bc-bar-track"><div className="bc-bar-fill" style={{width:'94%'}}></div></div>
            </div>
            <button className="copy-btn">Copy to EHR →</button>
          </div>
        </div>
        <div className="story-text reveal from-left">
          <div className="step-number">04</div>
          <div className="step-label">Step four</div>
          <h2>Bill what you earned.</h2>
          <p>CPT and ICD-10 suggestions generated directly from your documentation. No code-hunting required — AirScribe surfaces the right codes with confidence scores.</p>
        </div>
      </div>

      <div className="trust-band">
        <div className="stat-strip reveal">
          <div className="stat-item">
            <div className="stat-number">2+ hrs</div>
            <div className="stat-label">saved per clinician per day</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">&lt;60s</div>
            <div className="stat-label">from recording to complete note</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">20+</div>
            <div className="stat-label">specialties supported</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">$0</div>
            <div className="stat-label">always free to start</div>
          </div>
        </div>
        <div className="trust-band-grid">
          <div className="trust-band-card">
            <div className="trust-band-label">Security</div>
            <div className="trust-band-title">Encrypted by default</div>
            <div className="trust-band-copy">Audio and transcripts are end-to-end encrypted, so sensitive encounter data is protected throughout the workflow.</div>
          </div>
          <div className="trust-band-card">
            <div className="trust-band-label">Control</div>
            <div className="trust-band-title">Clinician stays in charge</div>
            <div className="trust-band-copy">AirScribe prepares structured notes and suggested codes, but the clinician reviews the output before it is copied into the chart.</div>
          </div>
          <div className="trust-band-card">
            <div className="trust-band-label">Privacy</div>
            <div className="trust-band-title">Your data stays your data</div>
            <div className="trust-band-copy">We do not use your clinical data to train models. If you need practice-specific compliance details, support and privacy contacts are available.</div>
          </div>
        </div>
      </div>

      <div className="section-divider"></div>

      {/* ── FAQ ── */}
      <div className="faq-section">
        <div className="faq-header reveal">
          <div className="step-label">FAQ</div>
          <h2>Common questions</h2>
        </div>
        <div className="faq-list">
          {[
            { q: 'How does voice-to-SOAP work?', a: 'Dictate naturally after a patient encounter — describe symptoms, findings, your assessment, and plan in any order. AirScribe transcribes in real time, then AI restructures it into standard S/O/A/P format.' },
            { q: 'Can I use it with my Apple Watch?', a: 'Yes. AirScribe records audio via Apple Watch so you can dictate hands-free. Recordings sync to your iPhone and generate notes automatically.' },
            { q: 'Does it suggest billing codes?', a: 'Yes. After your SOAP note is generated, AirScribe surfaces relevant CPT and ICD-10 codes with confidence scores. Always verify codes before submitting a claim.' },
            { q: 'Is it HIPAA compliant?', a: 'AirScribe is built with privacy in mind. Contact us at support@airscribe.app to discuss your specific compliance requirements.' },
            { q: 'Can I customize note templates?', a: 'Yes. The Presets section lets you write custom instructions that shape how AI formats your notes — useful for different specialties or documentation styles.' },
            { q: 'Is AirScribe free?', a: 'AirScribe is free to start. Paid plans are available for additional usage — you can try the full workflow before committing to anything.' },
          ].map(({ q, a }) => (
            <div key={q} className="faq-item reveal">
              <button className="faq-q" onClick={toggleFaq}>
                {q}
                <span className="faq-chevron">
                  <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                </span>
              </button>
              <div className="faq-a"><p>{a}</p></div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-divider"></div>

      {/* ── Final CTA ── */}
      <div className="final-cta">
        <h2 className="reveal">Less charting. More care.</h2>
        <p className="reveal">Join clinicians spending less time charting and more time with patients.</p>
        <a href="/login" className="btn-purple reveal" onClick={handleTryFree}>Get started</a>
      </div>

      </main>

      <footer>
        <div>&copy; 2026 AirScribe. All rights reserved.</div>
        <div className="footer-links">
          <a href="/legal/privacy" onClick={e => { e.preventDefault(); track('footer_privacy_click'); navigate('/legal/privacy'); }}>Privacy Policy</a>
          <a href="/legal/tos" onClick={e => { e.preventDefault(); track('footer_tos_click'); navigate('/legal/tos'); }}>Terms of Service</a>
        </div>
      </footer>
    </Box>
  );
}
